/**
 * Generates dummy form submission data for preview/DocuSign testing.
 *
 * Rules:
 * - Questions are filled in form traversal order (section → subsection → group → question)
 *   so that controlling questions are filled before their dependents are evaluated.
 * - A question is only filled if it is visible given the data filled so far (including
 *   parent section/subsection/group visibility).
 * - Default: questionRef is used as the value
 * - Date: uses a fixed YYYY-MM-DD date (2000-01-01)
 * - Options-based types (radio, select, multiselect, textselect): first option id is used
 * - Table: generates maxRows (or minRows, or 1) entries per column using the column id as value
 */

import { FormConditionalService } from '@client/services/FormConditionalService';

const DEFAULT_MAX_ROWS = 1;
const PREVIEW_DATE = '2000-01-01';

function valueForQuestion(
  questionRef: string,
  question: PolicyFormQuestion,
): PolicyFormSubmissionDataValue {
  switch (question.type) {
    case 'radio':
    case 'select':
    case 'multiselect':
    case 'textselect': {
      const firstOption = question.options?.[0];
      return firstOption ? firstOption.id : questionRef;
    }

    case 'date':
      return PREVIEW_DATE;

    case 'table': {
      const rowCount = question.maxRows ?? question.minRows ?? DEFAULT_MAX_ROWS;
      const columns = question.columns ?? [];
      const rows: Record<string, string>[] = [];

      for (let i = 0; i < rowCount; i++) {
        const row: Record<string, string> = {};
        for (const col of columns) {
          if (col.type === 'radio' || col.type === 'select') {
            const firstOption = col.options?.[0];
            row[col.id] = firstOption ? firstOption.id : col.id;
          } else if (col.type === 'date') {
            row[col.id] = PREVIEW_DATE;
          } else {
            row[col.id] = col.id;
          }
        }
        rows.push(row);
      }

      return rows;
    }

    default:
      return questionRef;
  }
}

export function generatePreviewFormData(
  formDefinition: PolicyFormDefinition,
): PolicyFormSubmissionData {
  const data: PolicyFormSubmissionData = {};

  // FormConditionalService is updated with data as we go, so each question's
  // visibility is evaluated against all previously filled values.
  const conditionalService = new FormConditionalService(formDefinition, data);

  for (const section of formDefinition.sections) {
    for (const subsection of section.subsections) {
      for (const group of subsection.questionGroups) {
        for (const questionRef of group.questions) {
          const question = formDefinition.questions[questionRef];
          if (!question) continue;

          conditionalService.update(data);
          const { visible } = conditionalService.getQuestionState(question);
          if (!visible) continue;

          data[questionRef] = valueForQuestion(questionRef, question);
        }
      }
    }
  }

  return data;
}
